/**
 * Service Selector Component
 *
 * Displays available additional services as selectable checkboxes.
 * Used in the order builder workflow to allow customers to add
 * optional services to their container order.
 *
 * @module components/widget/ServiceSelector
 */

import { WidgetService } from '../../types/order-builder';

export interface ServiceSelectorProps {
  /** List of available additional services */
  availableServices: WidgetService[];
  /** IDs of currently selected services */
  selectedServiceIds: string[];
  /** Callback when service selection is toggled */
  onServiceToggle: (serviceId: string) => void;
  /** Optional additional CSS classes */
  className?: string;
}

/**
 * ServiceSelector Component
 *
 * Renders a list of available services with checkboxes for selection.
 * Each service displays its name, optional description, and price.
 * Provides hover states and accessible keyboard navigation.
 */
export function ServiceSelector({
  availableServices,
  selectedServiceIds,
  onServiceToggle,
  className = '',
}: ServiceSelectorProps) {
  return (
    <div className={`space-y-3 ${className}`}>
      <h3 className="text-lg font-medium">Additional Services</h3>

      <div className="space-y-2">
        {availableServices.map((service) => (
          <label
            key={service.id}
            className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <input
              type="checkbox"
              checked={selectedServiceIds.includes(service.id)}
              onChange={() => onServiceToggle(service.id)}
              className="mt-1"
              aria-label={service.name}
            />
            <div className="flex-1">
              <div className="font-medium">{service.name}</div>
              {service.description && (
                <div className="text-sm text-gray-600 mt-1">{service.description}</div>
              )}
            </div>
            <div className="text-sm font-semibold text-green-600">
              +${(service.basePrice / 100).toFixed(2)}
            </div>
          </label>
        ))}
      </div>

      {availableServices.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-4">
          No additional services available
        </p>
      )}
    </div>
  );
}
